import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import moment from 'moment';
import { formatToDateTamplate } from '@/common/functions/dateFormater';
import { useRequestsContext } from '@/common/hooks/requestHook';
import { useCommonDataContext } from '@/common/hooks/commonDataContext';
import {
	analyzeCallByCallingInformationId,
	listCallAnalyses,
} from '@/API/repositories/callAnalysis';
import { CONSULTANT_ROLES } from '@/pages/login/components/LoginForm/loginForm';

export const YES_NO_OPTIONS = [
	{ label: 'Yes', value: 'true' },
	{ label: 'No', value: 'false' },
];

export const PAGE_SIZE = 50;

const useCallAnalysis = () => {
	const [items, setItems] = useState([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [selectedAnalysis, setSelectedAnalysis] = useState(null);
	const [consultants, setConsultants] = useState([]);
	const [managers, setManagers] = useState([]);

	const { makeRequest, hasUnfilledRequest } = useRequestsContext();
	const {
		filterUsersByRoles,
		commonData: { users },
		options: { marketsOptions },
		queryValues: { isFetching },
	} = useCommonDataContext();

	const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

	const handleSearch = async (values, nextPage = 1) => {
		const payload = {
			date_from: moment(values.fromDate).startOf('day').toISOString(),
			date_to: moment(values.toDate).endOf('day').toISOString(),
			limit: PAGE_SIZE,
			skip: (nextPage - 1) * PAGE_SIZE,
			consultant: values.consultant?.value,
			team_leader: values.team_leader?.value,
			market: values.market?.value,
			has_critical_error: values.has_critical_error?.value,
		};

		const response = await makeRequest(listCallAnalyses.bind(null, payload));

		if (response?.data) {
			setItems(response.data.items || []);
			setTotal(response.data.total || 0);
			setPage(nextPage);
		}
	};

	const formik = useFormik({
		initialValues: {
			fromDate: formatToDateTamplate(moment().subtract(7, 'days').toDate()),
			toDate: formatToDateTamplate(new Date()),
			consultant: null,
			team_leader: null,
			market: null,
			has_critical_error: null,
		},
		onSubmit: (values) => handleSearch(values, 1),
	});

	const openAnalysis = (analysis) => {
		setSelectedAnalysis(analysis);
	};

	const handleOpenOrAnalyze = async (analysis) => {
		if (analysis) {
			openAnalysis(analysis);
		}
	};

	const handleAnalyzeMissing = async (callingInformationId) => {
		const response = await makeRequest(
			analyzeCallByCallingInformationId.bind(
				null,
				callingInformationId,
				true,
			),
		);
		if (response?.data && !response.data.error) {
			setSelectedAnalysis(response.data);
			handleSearch(formik.values, page);
		}
	};

	const goToPage = (nextPage) => {
		const safePage = Math.min(Math.max(nextPage, 1), totalPages);
		handleSearch(formik.values, safePage);
	};

	const handleAnalysisUpdated = (updated) => {
		setSelectedAnalysis(updated);
		handleSearch(formik.values, page);
	};

	const isLoading =
		hasUnfilledRequest(listCallAnalyses, analyzeCallByCallingInformationId) ||
		isFetching;

	useEffect(() => {
		setConsultants(filterUsersByRoles(CONSULTANT_ROLES));
		setManagers(
			filterUsersByRoles(['CALL_CENTER_MANAGER', 'HEAD_CC_MANAGER']),
		);
	}, [users]);

	useEffect(() => {
		formik.submitForm();
	}, []);

	return {
		items,
		total,
		page,
		totalPages,
		selectedAnalysis,
		setSelectedAnalysis,
		consultants,
		managers,
		marketsOptions,
		formik,
		isLoading,
		handleOpenOrAnalyze,
		handleAnalyzeMissing,
		goToPage,
		handleAnalysisUpdated,
	};
};

export default useCallAnalysis;
